import { useCallback, useReducer, useRef } from 'react'

export interface GpsEditorHistorySnapshot {
  markers: CourseGpsMarker[]
  polygons: GpsEditorPolygon[]
  greenAutoMapPreview: {
    hole: number
    center: google.maps.LatLngLiteral
    edgePoints: google.maps.LatLngLiteral[]
  } | null
  greenAutoMapControlsVisible: boolean
}

const MAX_HISTORY_ENTRIES = 50

export interface GpsEditorHistory {
  recordBeforeChange: () => void
  undo: () => void
  redo: () => void
  reset: () => void
  canUndo: boolean
  canRedo: boolean
}

export function useGpsEditorHistory(
  getCurrentSnapshot: () => GpsEditorHistorySnapshot,
  restoreSnapshot: (snapshot: GpsEditorHistorySnapshot) => void,
): GpsEditorHistory {
  const undoStackRef = useRef<GpsEditorHistorySnapshot[]>([])
  const redoStackRef = useRef<GpsEditorHistorySnapshot[]>([])
  const [, bumpVersion] = useReducer((n: number) => n + 1, 0)

  const recordBeforeChange = useCallback(() => {
    const current = getCurrentSnapshot()
    const nextUndo = [current, ...undoStackRef.current]
    if (nextUndo.length > MAX_HISTORY_ENTRIES) {
      nextUndo.length = MAX_HISTORY_ENTRIES
    }
    undoStackRef.current = nextUndo
    redoStackRef.current = []
    bumpVersion()
  }, [getCurrentSnapshot])

  const undo = useCallback(() => {
    const [previous, ...rest] = undoStackRef.current
    if (!previous) {
      return
    }
    redoStackRef.current = [getCurrentSnapshot(), ...redoStackRef.current]
    undoStackRef.current = rest
    restoreSnapshot(previous)
    bumpVersion()
  }, [getCurrentSnapshot, restoreSnapshot])

  const redo = useCallback(() => {
    const [next, ...rest] = redoStackRef.current
    if (!next) {
      return
    }
    undoStackRef.current = [getCurrentSnapshot(), ...undoStackRef.current]
    redoStackRef.current = rest
    restoreSnapshot(next)
    bumpVersion()
  }, [getCurrentSnapshot, restoreSnapshot])

  const reset = useCallback(() => {
    undoStackRef.current = []
    redoStackRef.current = []
    bumpVersion()
  }, [])

  return {
    recordBeforeChange,
    undo,
    redo,
    reset,
    canUndo: undoStackRef.current.length > 0,
    canRedo: redoStackRef.current.length > 0,
  }
}
